import React, { useState } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

//import bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

//import css (reusing login styles mostly)
import "./ForgotPassword.css";

const ForgotPassword = () => {
  const [step, setStep] = useState(0); // 0 = Email, 1 = Code, 2 = New Password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const [errMsg, setErrMsg] = useState(false);
  const [errMsgContent, setErrMsgContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const serverUrl = `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_SERVER_HOST}:${process.env.REACT_APP_SERVER_HOST_PORT}`;
  const navigate = useNavigate();

  const handleRequestCode = (values: any) => {
    setErrMsg(false);
    setLoading(true);
    setEmail(values.email);

    axios
      .post(
        `${serverUrl}/api/auth/forgot-password/request`,
        { email: values.email },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then((response) => {
        if (response.data.status === "success" || (response.status === 200 && response.data.status !== "error")) {
          setStep(1);
        } else {
          setErrMsgContent(response.data.msg || "Failed to send reset link.");
          setErrMsg(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setErrMsgContent(error.response?.data?.msg || "Failed to send reset link.");
        setErrMsg(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleVerifyCode = (values: any) => {
    setErrMsg(false);
    setLoading(true);
    setCode(values.code);

    axios
      .post(
        `${serverUrl}/api/auth/forgot-password/verify`,
        { email, code: values.code },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then((response) => {
        if (response.data.status === "success" || (response.status === 200 && response.data.status !== "error")) {
          setStep(2);
        } else {
          setErrMsgContent(response.data.msg || "Invalid verification code.");
          setErrMsg(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setErrMsgContent(error.response?.data?.msg || "Invalid verification code.");
        setErrMsg(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetPassword = (values: any) => {
    setErrMsg(false);
    setLoading(true);

    axios
      .post(
        `${serverUrl}/api/auth/forgot-password/reset`,
        { email, code, newPassword: values.newPassword },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then((response) => {
        if (response.data.status === "success" || (response.status === 200 && response.data.status !== "error")) {
          setSuccessMsg("Password updated successfully!");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          setErrMsgContent(response.data.msg || "Failed to update password.");
          setErrMsg(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setErrMsgContent(error.response?.data?.msg || "Failed to update password.");
        setErrMsg(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="login-page">
      <Container className="login-container">
        <img className="qflo-login-logo-mobi" alt="logo" src="/media/images/qfloLoginMoi.png" />
        <Row className="test-row">
          <Col xs={12} md={6} className="login-col">
            <div className="login-div">
              <h1 style={{ fontSize: "32px" }}>Forgot Password</h1>

              {step === 0 && (
                <>
                  <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "30px" }}>Enter your email address and we'll send you a link to reset your password.</p>
                  <Formik
                    initialValues={{ email: "" }}
                    validationSchema={Yup.object({
                      email: Yup.string().email("Invalid email address").required("Required"),
                    })}
                    onSubmit={handleRequestCode}
                  >
                    <Form className="login-form">
                      <label>Email Address</label>
                      <Field name="email" type="email" placeholder="Enter your email" className="login-form-input" />
                      <p className="err-msg">
                        <ErrorMessage name="email" />
                      </p>

                      {errMsg && <p className="err-msg">{errMsgContent}</p>}

                      <Button className="login-btn" type="submit" disabled={loading}>
                        {loading ? "Sending..." : "Send Reset Link"}
                      </Button>
                    </Form>
                  </Formik>
                </>
              )}

              {step === 1 && (
                <>
                  <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "30px" }}>Enter the verification code sent to {email}.</p>
                  <Formik
                    initialValues={{ code: "" }}
                    validationSchema={Yup.object({
                      code: Yup.string().required("Required"),
                    })}
                    onSubmit={handleVerifyCode}
                  >
                    <Form className="login-form">
                      <label>Verification Code</label>
                      <Field name="code" type="text" placeholder="Enter code" className="login-form-input" />
                      <p className="err-msg">
                        <ErrorMessage name="code" />
                      </p>

                      {errMsg && <p className="err-msg">{errMsgContent}</p>}

                      <Button className="login-btn" type="submit" disabled={loading}>
                        {loading ? "Verifying..." : "Verify Code"}
                      </Button>
                    </Form>
                  </Formik>
                </>
              )}

              {step === 2 && (
                <>
                  <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "30px" }}>Enter your new password below.</p>
                  <Formik
                    initialValues={{ newPassword: "", confirmPassword: "" }}
                    validationSchema={Yup.object({
                      newPassword: Yup.string().required("Required"),
                      confirmPassword: Yup.string()
                        .oneOf([Yup.ref("newPassword"), undefined], "Passwords must match")
                        .required("Required"),
                    })}
                    onSubmit={handleResetPassword}
                  >
                    <Form className="login-form">
                      <label>New Password</label>
                      <Field name="newPassword" type="password" placeholder="Enter new password" className="login-form-input" />
                      <p className="err-msg">
                        <ErrorMessage name="newPassword" />
                      </p>

                      <label>Confirm Password</label>
                      <Field name="confirmPassword" type="password" placeholder="Confirm new password" className="login-form-input" />
                      <p className="err-msg">
                        <ErrorMessage name="confirmPassword" />
                      </p>

                      {errMsg && <p className="err-msg">{errMsgContent}</p>}
                      {successMsg && <p style={{ color: "green", fontSize: "14px", marginTop: "10px" }}>{successMsg}</p>}

                      <Button className="login-btn" type="submit" disabled={loading}>
                        {loading ? "Saving..." : "Save Password"}
                      </Button>
                    </Form>
                  </Formik>
                </>
              )}

              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <Button variant="link" onClick={() => navigate("/login")} style={{ color: "#122178", fontWeight: "600", textDecoration: "none" }}>
                  Back to Login
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={12} md={5} className="login-col-2">
            <img className="qflo-login-logo" alt="logo" src="/media/images/qfloLogin.png" />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgotPassword;
